import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useDispatch } from 'react-redux'
import { AlertCircle, CheckCircle2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  getImportWarnings,
  importGameAPI,
  importSectionAPI,
  validateImportAPI,
  type ImportPreviewResponse,
  type ImportScope,
  type PreviewItem,
  type ImportWarning,
  type SectionPreview,
} from '@/api/import'
import { groupImportWarnings } from '@/utils/groupImportWarnings'
import type { AppDispatch } from '@/redux/store'
import { disconnectAllAdvisorWebSockets } from '@/redux/asyncThunks/websocketThunks'

// Exports carry every advisor's document embeddings, so whole-game files run to hundreds of MB.
const MAX_FILE_MB = 500
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const SUPPORTED_VERSIONS = ['1.0', '1.1']
// Exports write export_version first, so the start of the file is enough to check it.
const VERSION_SCAN_BYTES = 64 * 1024
// Beyond this, parsing the whole file in the browser could freeze the tab.
const FULL_PARSE_MAX_BYTES = 20 * 1024 * 1024

async function readExportVersion(f: File): Promise<string | undefined> {
  const head = await f.slice(0, VERSION_SCAN_BYTES).text()
  if (!head.trimStart().startsWith('{')) throw new Error('Not a JSON object')
  const match = head.match(/"export_version"\s*:\s*"([^"]*)"/)
  if (match) return match[1]
  if (f.size > FULL_PARSE_MAX_BYTES) return undefined
  const data = JSON.parse(await f.text()) as { export_version?: string }
  return data.export_version
}

const SECTION_TITLE_OVERRIDES: Record<string, string> = {
  ai_player_threads: 'AI Player Chats',
  conference: 'Negotiations',
  adjudication_bot: 'Adjudicator bot',
  adjudication_chats: 'Adjudicator chats',
  decisions: 'Player decisions & adjudications',
  team_communicators: 'Team communicators',
  team_strategy_proposals: 'Strategy proposals',
  team_strategy_votes: 'Strategy votes',
  team_voting_statuses: 'Voting status',
}

function getSectionTitle(key: string): string {
  if (SECTION_TITLE_OVERRIDES[key]) return SECTION_TITLE_OVERRIDES[key]
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function getImportModalTitle(
  scope: ImportScope,
  mcpServersOnly?: boolean,
): string {
  if (mcpServersOnly && scope === 'advisors') return 'Import MCP'
  if (scope === 'full_game') return 'Import game'
  return `Import (${scope.replace(/_/g, ' ')})`
}

export interface ImportModalProps {
  isOpen: boolean
  onClose: () => void
  scope: ImportScope
  roomId?: string
  roomName?: string
  mcpServersOnly?: boolean
  onImportComplete?: (result?: { room_id?: string }) => void
}

function flattenPreviewItems(sections: Record<string, SectionPreview>): PreviewItem[] {
  const out: PreviewItem[] = []
  for (const sec of Object.values(sections)) {
    for (const it of sec.items || []) {
      out.push(it)
    }
  }
  return out
}

function collectAllIds(sections: Record<string, SectionPreview>): Set<string> {
  return new Set(flattenPreviewItems(sections).map((i) => i.id))
}

const DEFER_DEFAULT_SELECTION = 'defer_default_selection'
const AUTO_INCLUDE_WITH_PROVISION = 'auto_include_with_provision'

function collectDeferDefaultIds(sections: Record<string, SectionPreview>): Set<string> {
  const out = new Set<string>()
  for (const it of flattenPreviewItems(sections)) {
    if (it.flags?.includes(DEFER_DEFAULT_SELECTION)) {
      out.add(it.id)
    }
  }
  return out
}

function collectAutoIncludeWithProvisionIds(
  sections: Record<string, SectionPreview>,
): Set<string> {
  const out = new Set<string>()
  for (const it of flattenPreviewItems(sections)) {
    if (it.flags?.includes(AUTO_INCLUDE_WITH_PROVISION)) {
      out.add(it.id)
    }
  }
  return out
}

function eventAnalystEmailFromPreviewItem(item: PreviewItem): string | null {
  const match = /\(([^)]+@[^)]+)\)\s*$/.exec(item.label)
  return match ? match[1].toLowerCase() : null
}

const PHASE_RANDR = 'randr'

function isAdvisorSelectionId(id: string): boolean {
  return (
    id.startsWith('advisor:') &&
    !id.startsWith('advisor_chat:') &&
    !id.startsWith('advisor_mcp:')
  )
}

function isDerivedEventAnalystPreviewItem(item: PreviewItem): boolean {
  return (
    item.id.startsWith('event_analyst:') &&
    eventAnalystEmailFromPreviewItem(item) !== null
  )
}

function isRandrAdvisorChatItem(item: PreviewItem): boolean {
  return item.phase_type === PHASE_RANDR
}

function derivedEventAnalystSelectedForEmail(
  includedIds: Set<string>,
  sections: Record<string, SectionPreview>,
  email: string,
): boolean {
  const normalized = email.toLowerCase()
  for (const it of flattenPreviewItems(sections)) {
    if (!isDerivedEventAnalystPreviewItem(it)) continue
    if (
      eventAnalystEmailFromPreviewItem(it) === normalized &&
      includedIds.has(it.id)
    ) {
      return true
    }
  }
  return false
}

function isAdjudicationBotSelected(includedIds: Set<string>): boolean {
  return includedIds.has('adjudication_bot:config')
}

function isAdjudicationChatItemDisabled(
  item: PreviewItem,
  includedIds: Set<string>,
): boolean {
  if (!item.id.startsWith('adjudication_chat:')) return false
  if (!isAdjudicationBotSelected(includedIds)) return true
  if (item.parent_id) {
    return !includedIds.has(item.parent_id)
  }
  return true
}

function syncAdjudicationChatsForDecisionId(
  ids: Set<string>,
  sections: Record<string, SectionPreview>,
  decisionId: string,
  add: boolean,
): void {
  for (const item of flattenPreviewItems(sections)) {
    if (!item.id.startsWith('adjudication_chat:')) continue
    if (item.parent_id !== decisionId) continue
    if (add) {
      ids.add(item.id)
    } else {
      ids.delete(item.id)
    }
  }
}

function filterIneligibleAdjudicationChatsFromSet(
  ids: Set<string>,
  sections: Record<string, SectionPreview>,
): Set<string> {
  const out = new Set(ids)
  for (const item of flattenPreviewItems(sections)) {
    if (!item.id.startsWith('adjudication_chat:')) continue
    if (isAdjudicationChatItemDisabled(item, out)) {
      out.delete(item.id)
    }
  }
  return out
}

function isAdvisorChatItemDisabled(
  item: PreviewItem,
  includedIds: Set<string>,
  sections: Record<string, SectionPreview>,
): boolean {
  if (!item.id.startsWith('advisor_chat:')) return false
  if (isRandrAdvisorChatItem(item)) {
    const email = (item.sender_email || '').toLowerCase()
    if (!email) return true
    if (item.parent_id) {
      return !includedIds.has(item.parent_id)
    }
    return !derivedEventAnalystSelectedForEmail(includedIds, sections, email)
  }
  return !hasAnySelectedAdvisors(includedIds, sections)
}

function filterIneligibleAdvisorChatsFromSet(
  ids: Set<string>,
  sections: Record<string, SectionPreview>,
): Set<string> {
  const out = new Set(ids)
  for (const item of flattenPreviewItems(sections)) {
    if (!item.id.startsWith('advisor_chat:')) continue
    if (isAdvisorChatItemDisabled(item, out, sections)) {
      out.delete(item.id)
    }
  }
  return out
}

function syncRandrChatsForDerivedEaEmail(
  ids: Set<string>,
  sections: Record<string, SectionPreview>,
  email: string,
  add: boolean,
): void {
  const normalized = email.toLowerCase()
  for (const item of flattenPreviewItems(sections)) {
    if (!item.id.startsWith('advisor_chat:') || !isRandrAdvisorChatItem(item)) {
      continue
    }
    const chatEmail = (item.sender_email || '').toLowerCase()
    if (chatEmail !== normalized) continue
    if (add) {
      ids.add(item.id)
    } else {
      ids.delete(item.id)
    }
  }
}

function hasAnySelectedAdvisors(
  includedIds: Set<string>,
  sections: Record<string, SectionPreview> | undefined,
): boolean {
  if (!sections) return false
  for (const it of flattenPreviewItems(sections)) {
    if (isAdvisorSelectionId(it.id) && includedIds.has(it.id)) {
      return true
    }
  }
  return false
}

function applyProvisionToIncludedIds(
  prev: Set<string>,
  sections: Record<string, SectionPreview>,
  provisionMissingStudents: boolean,
): Set<string> {
  const autoInclude = collectAutoIncludeWithProvisionIds(sections)
  const next = new Set(prev)
  if (provisionMissingStudents) {
    for (const id of autoInclude) {
      next.add(id)
    }
  } else {
    for (const id of autoInclude) {
      next.delete(id)
    }
  }
  return next
}

function collectInitialIncludedIds(
  sections: Record<string, SectionPreview>,
  scope: ImportScope | string,
  provisionMissingStudents = false,
): Set<string> {
  const all = collectAllIds(sections)
  if (scope !== 'full_game') return all
  const deferred = collectDeferDefaultIds(sections)
  const out = new Set<string>()
  for (const id of all) {
    if (deferred.has(id)) continue
    out.add(id)
  }
  return applyProvisionToIncludedIds(out, sections, provisionMissingStudents)
}

type PendingImportAck =
  | null
  | { kind: 'full_game'; roomId: string }
  | { kind: 'section' }

function previewHasMissingStudentAccounts(
  sections: Record<string, SectionPreview> | undefined,
): boolean {
  const fromPlayers = sections?.players?.items?.some((it) =>
    it.flags?.includes('no_account'),
  )
  const fromAiThreads = sections?.ai_player_threads?.items?.some((it) =>
    it.flags?.includes('no_account'),
  )
  return Boolean(fromPlayers || fromAiThreads)
}

export function ImportModal({
  isOpen,
  onClose,
  scope,
  roomId,
  roomName,
  mcpServersOnly = false,
  onImportComplete,
}: ImportModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [file, setFile] = useState<File | null>(null)
  const [parsedVersion, setParsedVersion] = useState<string | null>(null)
  const [localParseError, setLocalParseError] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null)
  const [includedIds, setIncludedIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState<boolean>(false)
  const [importing, setImporting] = useState<boolean>(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [provisionMissingStudents, setProvisionMissingStudents] = useState<boolean>(false)
  const [importWarnings, setImportWarnings] = useState<ImportWarning[]>([])
  const [completedRoomShortId, setCompletedRoomShortId] = useState<string | null>(null)

  const pendingImportAckRef = useRef<PendingImportAck>(null)

  const reset = useCallback(() => {
    setStep(1)
    setFile(null)
    setParsedVersion(null)
    setLocalParseError(null)
    setPreview(null)
    setIncludedIds(new Set())
    setLoading(false)
    setImporting(false)
    setServerError(null)
    setProvisionMissingStudents(false)
    setImportWarnings([])
    setCompletedRoomShortId(null)
    pendingImportAckRef.current = null
  }, [])

  const flushPendingImportAck = useCallback(() => {
    const p = pendingImportAckRef.current
    if (p === null) return
    if (p.kind === 'full_game') {
      onImportComplete?.({ room_id: p.roomId })
    } else {
      onImportComplete?.()
    }
    pendingImportAckRef.current = null
  }, [onImportComplete])

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      flushPendingImportAck()
      reset()
      onClose()
    }
  }

  const finishImportWizard = () => {
    handleOpenChange(false)
  }

  const onPickFile = async (f: File | null) => {
    setFile(f)
    setLocalParseError(null)
    setParsedVersion(null)
    setServerError(null)
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setLocalParseError(`File exceeds ${MAX_FILE_MB} MB.`)
      return
    }
    if (!f.name.toLowerCase().endsWith('.json')) {
      setLocalParseError('Please choose a .json file.')
      return
    }
    try {
      const v = await readExportVersion(f)
      if (!v || !SUPPORTED_VERSIONS.includes(String(v))) {
        setLocalParseError(
          `Unsupported export_version: ${v ?? 'missing'}. Expected one of ${SUPPORTED_VERSIONS.join(', ')}.`
        )
        return
      }
      setParsedVersion(String(v))
    } catch {
      setLocalParseError('Could not parse JSON.')
    }
  }

  const runValidate = async (provisionForPreview = false) => {
    if (!file || localParseError || !parsedVersion) return
    setLoading(true)
    setServerError(null)
    try {
      const res = await validateImportAPI(file, scope, roomId, {
        mcpServersOnly,
        provisionMissingStudentAccounts: provisionForPreview,
      })
      setPreview(res)
      setIncludedIds(
        collectInitialIncludedIds(res.sections, scope, provisionForPreview),
      )
      setStep(2)
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Validation failed')
    } finally {
      setLoading(false)
    }
  }

  const refreshPreviewForProvision = useCallback(
    async (provisionForPreview: boolean) => {
      if (!file || localParseError || !parsedVersion || step !== 2) return
      setLoading(true)
      setServerError(null)
      try {
        const res = await validateImportAPI(file, scope, roomId, {
          mcpServersOnly,
          provisionMissingStudentAccounts: provisionForPreview,
        })
        setPreview(res)
        setIncludedIds((prev) =>
          applyProvisionToIncludedIds(prev, res.sections, provisionForPreview),
        )
      } catch (e) {
        setServerError(e instanceof Error ? e.message : 'Validation failed')
      } finally {
        setLoading(false)
      }
    },
    [file, localParseError, parsedVersion, step, scope, roomId, mcpServersOnly],
  )

  const handleProvisionMissingStudentsChange = (checked: boolean) => {
    setProvisionMissingStudents(checked)
    void refreshPreviewForProvision(checked)
  }

  const toggleId = (id: string) => {
    setIncludedIds((prev) => {
      const sections = preview?.sections ?? {}
      const n = new Set(prev)
      const removing = n.has(id)
      if (removing) {
        n.delete(id)
        if (id.startsWith('player:')) {
          const email = id.slice('player:'.length).toLowerCase()
          for (const item of flattenPreviewItems(sections)) {
            if (eventAnalystEmailFromPreviewItem(item) === email) {
              n.delete(item.id)
            }
          }
          syncRandrChatsForDerivedEaEmail(n, sections, email, false)
        }
        if (id.startsWith('event_analyst:')) {
          for (const item of flattenPreviewItems(sections)) {
            if (item.id !== id || !isDerivedEventAnalystPreviewItem(item)) {
              continue
            }
            const email = eventAnalystEmailFromPreviewItem(item)
            if (email) {
              syncRandrChatsForDerivedEaEmail(n, sections, email, false)
            }
            break
          }
        }
        if (isAdvisorSelectionId(id) && !hasAnySelectedAdvisors(n, sections)) {
          for (const item of flattenPreviewItems(sections)) {
            if (item.id.startsWith('advisor_chat:') && !isRandrAdvisorChatItem(item)) {
              n.delete(item.id)
            }
          }
        }
        if (id === 'adjudication_bot:config') {
          for (const item of flattenPreviewItems(sections)) {
            if (item.id.startsWith('adjudication_chat:')) {
              n.delete(item.id)
            }
          }
        }
        if (id.startsWith('decision:')) {
          syncAdjudicationChatsForDecisionId(n, sections, id, false)
        }
      } else {
        n.add(id)
        if (id.startsWith('player:')) {
          const email = id.slice('player:'.length).toLowerCase()
          for (const item of flattenPreviewItems(sections)) {
            if (eventAnalystEmailFromPreviewItem(item) === email) {
              n.add(item.id)
            }
          }
          syncRandrChatsForDerivedEaEmail(n, sections, email, true)
        }
        if (id.startsWith('event_analyst:')) {
          for (const item of flattenPreviewItems(sections)) {
            if (item.id !== id || !isDerivedEventAnalystPreviewItem(item)) {
              continue
            }
            const email = eventAnalystEmailFromPreviewItem(item)
            if (email) {
              syncRandrChatsForDerivedEaEmail(n, sections, email, true)
            }
            break
          }
        }
        if (id === 'adjudication_bot:config') {
          for (const item of flattenPreviewItems(sections)) {
            if (
              item.id.startsWith('adjudication_chat:') &&
              item.parent_id &&
              n.has(item.parent_id)
            ) {
              n.add(item.id)
            }
          }
        }
        if (id.startsWith('decision:') && isAdjudicationBotSelected(n)) {
          syncAdjudicationChatsForDecisionId(n, sections, id, true)
        }
      }
      return filterIneligibleAdjudicationChatsFromSet(
        filterIneligibleAdvisorChatsFromSet(n, sections),
        sections,
      )
    })
  }

  const sectionEntries = useMemo(() => {
    if (!preview) return []
    return Object.entries(preview.sections)
  }, [preview])

  const confirmSummary = useMemo(() => {
    if (!preview) return { n: 0, m: 0 }
    let n = 0
    let m = 0
    for (const [, sec] of Object.entries(preview.sections)) {
      m += 1
      for (const it of sec.items) {
        if (includedIds.has(it.id)) n += 1
      }
    }
    return { n, m }
  }, [preview, includedIds])

  const showProvisionOption = useMemo(() => {
    if (scope !== 'full_game' && scope !== 'players_teams') return false
    return previewHasMissingStudentAccounts(preview?.sections)
  }, [scope, preview])

  const importsPlayers = scope === 'full_game' || scope === 'players_teams'

  const groupedImportWarnings = useMemo(
    () => groupImportWarnings(importWarnings),
    [importWarnings],
  )

  const runImport = async () => {
    if (!file) return
    setImporting(true)
    setServerError(null)
    if (scope === 'full_game') {
      try {
        await dispatch(disconnectAllAdvisorWebSockets()).unwrap()
      } catch (e) {
        const message =
          typeof e === 'string'
            ? e
            : e instanceof Error
              ? e.message
              : 'Could not disconnect advisor connections'
        toast.warning(`${message}. Import will continue.`)
      }
    }
    try {
      const effectiveIncludedIds = filterIneligibleAdvisorChatsFromSet(
        includedIds,
        preview?.sections ?? {},
      )
      const selections = {
        included_item_ids: Array.from(effectiveIncludedIds),
        mcp_servers_only: mcpServersOnly,
        replace_rounds: scope === 'rounds_timers',
        ...(importsPlayers && provisionMissingStudents
          ? { provision_missing_student_accounts: true }
          : {}),
      }
      if (scope === 'full_game') {
        const data = await importGameAPI(file, {
          ...selections,
        })
        const warns = getImportWarnings(data)
        if (warns.length > 0) {
          pendingImportAckRef.current = {
            kind: 'full_game',
            roomId: data.room_id,
          }
          setCompletedRoomShortId(data.room_id)
          setImportWarnings(warns)
          setStep(4)
          return
        }
        toast.success(`Game imported. Room ID: ${data.room_id}`)
        onImportComplete?.({ room_id: data.room_id })
      } else {
        if (!roomId) throw new Error('Room ID required')
        const data = await importSectionAPI(roomId, scope, file, selections, {
          mcpServersOnly,
        })
        const warns = getImportWarnings(data)
        if (warns.length > 0) {
          pendingImportAckRef.current = { kind: 'section' }
          setCompletedRoomShortId(null)
          setImportWarnings(warns)
          setStep(4)
          return
        }
        toast.success('Import completed.')
        onImportComplete?.()
      }
      reset()
      onClose()
    } catch (e) {
      setServerError(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  const title = getImportModalTitle(scope, mcpServersOnly)

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-lg max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{step === 4 ? 'Import complete' : title}</DialogTitle>
          {roomName && scope !== 'full_game' && step !== 4 && (
            <p className='text-sm text-muted-foreground'>Room: {roomName}</p>
          )}
        </DialogHeader>

        {step === 1 && (
          <div className='space-y-4 py-2'>
            <div
              className='border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-slate-50'
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                const f = e.dataTransfer.files?.[0]
                void onPickFile(f || null)
              }}
            >
              <input
                type='file'
                accept='.json,application/json'
                className='hidden'
                id='import-file-input'
                onChange={(e) => void onPickFile(e.target.files?.[0] || null)}
              />
              <label htmlFor='import-file-input' className='cursor-pointer text-sm'>
                Drop export JSON here or click to browse
              </label>
              {file && (
                <p className='mt-2 text-xs text-slate-600 truncate'>{file.name}</p>
              )}
            </div>
            {localParseError && (
              <div className='rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800'>
                {localParseError}
              </div>
            )}
            {serverError && (
              <div className='rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800'>
                {serverError}
              </div>
            )}
            {scope === 'ai_players' && parsedVersion === '1.0' && (
              <div className='rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900'>
                v1.0 exports only include minimal AI player rows (name / team). Full
                configuration requires a v1.1 export.
              </div>
            )}
          </div>
        )}

        {step === 2 && preview && (
          <div className='space-y-4 py-2 max-h-[55vh] overflow-y-auto pr-1'>
            {sectionEntries.map(([key, sec]) => {
              const sections = preview.sections
              return (
              <div key={key} className='border rounded-lg p-3'>
                <div className='flex items-center justify-between gap-2 mb-2'>
                  <span className='text-sm font-semibold'>
                    {getSectionTitle(key)}
                  </span>
                  <span className='text-xs text-slate-500'>
                    {sec.count} items
                    {sec.duplicate_count > 0 && (
                      <span className='ml-2 text-amber-700'>
                        {sec.duplicate_count} duplicate
                        {sec.duplicate_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </span>
                </div>
                <div className='space-y-2'>
                  {sec.items.map((it) => {
                    const advisorChatDisabled = isAdvisorChatItemDisabled(
                      it,
                      includedIds,
                      sections,
                    )
                    const adjudicationChatDisabled = isAdjudicationChatItemDisabled(
                      it,
                      includedIds,
                    )
                    const itemDisabled =
                      advisorChatDisabled || adjudicationChatDisabled
                    const checked =
                      !itemDisabled && includedIds.has(it.id)
                    const gateHint = itemDisabled
                      ? adjudicationChatDisabled
                        ? !isAdjudicationBotSelected(includedIds)
                          ? 'Requires the Adjudicator bot to be selected.'
                          : 'Requires the parent decision to be selected.'
                        : isRandrAdvisorChatItem(it)
                          ? 'Requires the Event Analyst for this player to be selected.'
                          : 'Requires at least one advisor to be selected.'
                      : null
                    return (
                    <div
                      key={it.id}
                      className={`flex items-start gap-2 text-sm ${it.parent_id ? 'ml-4' : ''}`}
                    >
                      <input
                        type='checkbox'
                        checked={checked}
                        disabled={itemDisabled}
                        onChange={() => toggleId(it.id)}
                        className='mt-1 h-4 w-4 rounded border-gray-300 accent-blue-700 disabled:cursor-not-allowed disabled:opacity-50'
                      />
                      <label className='flex-1 leading-snug cursor-pointer'>
                        <span>{it.label}</span>
                        {it.is_duplicate && (
                          <span className='ml-2 text-xs text-amber-700'>Already exists</span>
                        )}
                        {gateHint && (
                          <span className='mt-0.5 block text-xs text-slate-500'>
                            {gateHint}
                          </span>
                        )}
                        {it.hint && (
                          <span
                            className={`mt-0.5 block text-xs ${
                              it.severity === 'warn'
                                ? 'text-amber-800'
                                : 'text-slate-500'
                            }`}
                          >
                            {it.hint}
                          </span>
                        )}
                      </label>
                    </div>
                    )
                  })}
                </div>
                {key === 'players' && showProvisionOption ? (
                  <label className='flex items-start gap-2 mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm cursor-pointer'>
                    <input
                      type='checkbox'
                      checked={provisionMissingStudents}
                      onChange={(e) => handleProvisionMissingStudentsChange(e.target.checked)}
                      disabled={loading}
                      className='mt-1 h-4 w-4 rounded border-gray-300 accent-blue-700'
                    />
                    <span className='leading-snug text-slate-700'>
                      <span className='font-medium'>Sending invitations</span>
                      {' — '}For emails not yet registered on the platform, send invites and add
                      members.
                    </span>
                  </label>
                ) : null}
              </div>
            )
            })}
            {serverError ? (
              <div className='rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800'>
                {serverError}
              </div>
            ) : null}
          </div>
        )}

        {step === 3 && preview && (
          <div className='py-2 text-sm text-slate-700'>
            Importing <strong>{confirmSummary.n}</strong> item
            {confirmSummary.n === 1 ? '' : 's'} across{' '}
            <strong>{confirmSummary.m}</strong> section
            {confirmSummary.m === 1 ? '' : 's'}
            {roomName ? ` into ${roomName}` : ''}.
          </div>
        )}

        {step === 4 && (
          <div className='space-y-4 py-2'>
            <Alert className='border-emerald-200 bg-emerald-50/95 text-emerald-950 [&>svg]:text-emerald-700'>
              <CheckCircle2 className='h-4 w-4' />
              <AlertTitle className='text-sm font-semibold'>
                {scope === 'full_game' ? 'Game imported successfully' : 'Import finished successfully'}
              </AlertTitle>
              <AlertDescription className='text-sm text-foreground space-y-1 mt-2'>
                {scope === 'full_game' && completedRoomShortId ? (
                  <p>
                    <span className='font-medium'>Room ID:</span>{' '}
                    <span className='tabular-nums'>{completedRoomShortId}</span>
                  </p>
                ) : (
                  <p>{roomName ? `Updates were applied to ${roomName}.` : 'Updates were applied to this room.'}</p>
                )}
              </AlertDescription>
            </Alert>

            {groupedImportWarnings.length > 0 && (
              <Alert className='border-amber-200 bg-amber-50/90 text-amber-950 [&>svg]:text-amber-600'>
                <AlertCircle className='h-4 w-4' />
                <AlertTitle className='text-sm font-semibold'>
                  Missing files · {importWarnings.length}{' '}
                  {importWarnings.length === 1 ? 'notice' : 'notices'}
                </AlertTitle>
                <AlertDescription>
                  <p className='text-sm text-foreground mb-3 mt-1'>
                    Some media or documents from the export could not be copied (
                    unavailable or unreachable in storage). Events and advisors were created;
                    attach files again where needed.
                  </p>
                  <div className='max-h-[220px] overflow-y-auto mt-2 pr-2 border-t border-amber-300/70 pt-3 space-y-3'>
                    {groupedImportWarnings.map((section) => (
                      <div key={section.id}>
                        <p className='text-sm font-semibold text-foreground'>{section.title}</p>
                        <ul className='list-disc list-inside space-y-1 text-sm text-foreground mt-1'>
                          {section.bullets.map((line, idx) => (
                            <li key={idx} className='leading-snug'>
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter className='gap-2 flex-wrap'>
          {step > 1 && step < 4 && (
            <Button
              type='button'
              variant='outline'
              disabled={importing}
              onClick={() => setStep((s) => (s === 3 ? 2 : 1) as 1 | 2 | 3)}
            >
              Back
            </Button>
          )}
          <Button type='button' variant='outline' disabled={importing} onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          {step === 1 && (
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={!file || !!localParseError || !parsedVersion || loading}
              onClick={() => void runValidate(provisionMissingStudents)}
            >
              {loading ? 'Checking…' : 'Next'}
            </Button>
          )}
          {step === 2 && (
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              onClick={() => setStep(3)}
              disabled={includedIds.size === 0}
            >
              Next
            </Button>
          )}
          {step === 3 && (
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={importing || includedIds.size === 0}
              onClick={() => void runImport()}
            >
              {importing ? 'Importing…' : 'Import'}
            </Button>
          )}
          {step === 4 && (
            <Button
              className='bg-dark-blue hover:bg-dark-blue-hovered'
              disabled={importing}
              type='button'
              onClick={finishImportWizard}
            >
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
